import { useCallback, useEffect, useRef, useState } from "react"
import { Box, Stack, Typography } from "@mui/material"
import { useConteudoImpressaoTotemMutation, useEmitirNotaTotemMutation, useGetLojaAtualQuery, useImprimirResumoTotemMutation, type StatusNotaFiscalTotem } from "@/api"
import { getLocalImpressaoTotem } from "@/lib/totem-config"
import { chaveDaTransacao, imprimeNoAparelho, imprimirComInsistencia, temImpressoraNoAparelho, type ImpressaoCupomNota } from "@/lib/impressao-totem"
import { BlocoNotaFiscalTotem } from "@/components/totem/nota-fiscal-totem"
import AvisoInatividade from "@/components/totem/aviso-inatividade"
import PosPagamentoLayout from "./pos-pagamento-layout"
import CpfPergunta from "./cpf-pergunta"
import CpfInput from "./cpf-input"
import ImpressaoPergunta from "./impressao-pergunta"
import PagamentoRealizado from "./pagamento-realizado"
import type { EtapaPosPagamento } from "../passos-pagamento"

type Etapa = EtapaPosPagamento

const SEGUNDOS_ESPERA_NFCE = 30

export const INATIVIDADE_PERGUNTA_MS = 30_000

interface PosPagamentoProps {
    idPedidoIntegracao: number | null
    valorFormatado: string
    formaRotulo: string
    nsu: string | null
    emiteNfce: boolean
    impressao: 'NENHUMA' | 'RESUMO' | 'NFCE'
    /** Totem configurado para imprimir a nota sempre: não pergunta ao cliente se ele quer o papel. */
    imprimirNotaSempre?: boolean
    /** Totem que pergunta o CPF da nota; desligado, a nota sai sem CPF. */
    perguntarCpf?: boolean
    avisoRegistroPendente?: string | null
    onConcluir: () => void
    /** O stepper da tela de pagamento acende o passo pela etapa. */
    aoMudarEtapa?: (etapa: Etapa) => void
}

export default function PosPagamento({ idPedidoIntegracao, valorFormatado, formaRotulo, nsu, emiteNfce, impressao, imprimirNotaSempre = false, perguntarCpf = true, avisoRegistroPendente = null, onConcluir, aoMudarEtapa }: PosPagamentoProps): JSX.Element {
    const ofereceNfce = impressao === 'NFCE' && emiteNfce
    const imprimeResumo = impressao === 'RESUMO'
    // Sem CPF a perguntar, a nota começa pela impressão; com "imprimir sempre" também, não há o que perguntar e ela já sai.
    const etapaDaNota: Etapa = perguntarCpf ? 'cpf-pergunta' : imprimirNotaSempre ? 'emitindo' : 'impressao-pergunta'
    const [etapa, setEtapa] = useState<Etapa>(ofereceNfce ? etapaDaNota : imprimeResumo ? 'emitindo' : 'final')
    const [cpf, setCpf] = useState('')
    const [imprimiu, setImprimiu] = useState(false)
    const [querCupom, setQuerCupom] = useState(false)
    const [statusNota, setStatusNota] = useState<StatusNotaFiscalTotem | null>(null)
    const [avisoEmissao, setAvisoEmissao] = useState<string | null>(null)
    const [emitirNota] = useEmitirNotaTotemMutation()
    const [imprimirResumo] = useImprimirResumoTotemMutation()
    const [buscarConteudoImpressao] = useConteudoImpressaoTotemMutation()
    const { data: loja } = useGetLojaAtualQuery()
    const servidorImpressao = loja?.impressaoLocal?.servidor ?? null
    const emitindoRef = useRef(false)
    const [restante, setRestante] = useState(SEGUNDOS_ESPERA_NFCE)

    useEffect(() => {
        aoMudarEtapa?.(etapa)
    }, [etapa, aoMudarEtapa])

    const imprimirNoAparelho = useCallback(async (documento: 'RESUMO' | 'NFCE', idPedido: number): Promise<boolean> => {
        const desfecho = await imprimirComInsistencia(
            `checkout:${documento}:${idPedido}:${chaveDaTransacao(nsu)}`,
            () => buscarConteudoImpressao({ idPedidoIntegracao: idPedido, documento }).unwrap().then((r) => r.documentos),
            servidorImpressao,
        )

        return desfecho === 'IMPRESSO' || desfecho === 'JA_IMPRESSO'
    }, [buscarConteudoImpressao, servidorImpressao, nsu])

    const emitir = useCallback(async (imprimir: boolean, cpfDigitos: string): Promise<void> => {
        if (emitindoRef.current) return

        emitindoRef.current = true
        setQuerCupom(imprimir)
        setEtapa('emitindo')

        if (idPedidoIntegracao == null) {
            setStatusNota({ situacao: 'ERRO', numero: null, mensagem: 'Pedido não identificado no Sischef.' })
            setEtapa('final')
            return
        }

        try {
            const noAparelho = imprimeNoAparelho()
            const resultado = await emitirNota({
                imprimirComoLinhas: await temImpressoraNoAparelho(),
                idPedidoIntegracao,
                cpf: cpfDigitos || null,
                imprimir: imprimir && !noAparelho,
                localImpressao: noAparelho ? null : getLocalImpressaoTotem() || null,
            }).unwrap()

            if (resultado.sucesso ?? resultado.aprovada) {
                const impressa = noAparelho
                    ? imprimir && await imprimirNoAparelho('NFCE', idPedidoIntegracao)
                    : resultado.impressa ?? imprimir

                setImprimiu(impressa)
                setStatusNota({ situacao: 'AUTORIZADA', numero: null, mensagem: resultado.mensagem ?? null })
            } else {
                console.warn('[nfce-totem] nota não autorizada', resultado.mensagem)
                setStatusNota({ situacao: 'ERRO', numero: null, mensagem: resultado.mensagem ?? null })
            }
        } catch (err) {
            console.error('[nfce-totem] falha ao emitir nota fiscal', err)
            setStatusNota({ situacao: 'ERRO', numero: null, mensagem: null })
        } finally {
            setEtapa('final')
        }
    }, [idPedidoIntegracao, emitirNota, imprimirNoAparelho])

    useEffect(() => {
        if (ofereceNfce && !perguntarCpf && imprimirNotaSempre) void emitir(true, '')
    }, [ofereceNfce, perguntarCpf, imprimirNotaSempre, emitir])

    useEffect(() => {
        if (etapa !== 'emitindo') return

        const intervalo = window.setInterval(() => setRestante((r) => Math.max(0, r - 1)), 1000)

        return () => window.clearInterval(intervalo)
    }, [etapa])

    useEffect(() => {
        if (!imprimeResumo || emitindoRef.current) return
        emitindoRef.current = true

        void (async () => {
            if (idPedidoIntegracao == null) {
                setEtapa('final')
                return
            }
            try {
                const impressa = imprimeNoAparelho()
                    ? await imprimirNoAparelho('RESUMO', idPedidoIntegracao)
                    : !!(await imprimirResumo({
                        idPedidoIntegracao,
                        localImpressao: getLocalImpressaoTotem() || null,
                    }).unwrap()).impressa

                setImprimiu(impressa)
                if (!impressa) setAvisoEmissao('Não foi possível imprimir o resumo. Chame um atendente.')
            } catch (err) {
                console.error('[resumo-totem] falha ao imprimir o resumo', err)
                setAvisoEmissao('Não foi possível imprimir o resumo. Chame um atendente.')
            } finally {
                setEtapa('final')
            }
        })()
    }, [imprimeResumo, idPedidoIntegracao, imprimirResumo, imprimirNoAparelho])

    // O que a tela diz do cupom: o cliente escolheu, e a impressão pode ter falhado.
    const impressaoCupom: ImpressaoCupomNota = !querCupom ? 'SEM_CUPOM' : etapa === 'final' && !imprimiu ? 'FALHOU' : 'AUTOMATICA'

    // Sem resposta, a venda paga não fica sem nota: sai sem cupom e só leva o CPF que o cliente confirmou.
    const cpfConfirmado = etapa === 'impressao-pergunta' ? cpf : ''
    const seguirSemResposta = (): void => void emitir(imprimirNotaSempre, cpfConfirmado)

    // Respondido o CPF: com "imprimir sempre" a nota já sai impressa; senão o cliente escolhe o papel.
    const seguirDoCpf = (cpfInformado: string): void => {
        if (imprimirNotaSempre) {
            void emitir(true, cpfInformado)
            return
        }

        setCpf(cpfInformado)
        setEtapa('impressao-pergunta')
    }

    if (etapa === 'final') {
        return (
            <PagamentoRealizado
                valorFormatado={valorFormatado}
                formaRotulo={formaRotulo}
                nsu={nsu}
                imprimiu={imprimiu}
                statusNota={statusNota}
                impressaoCupom={impressaoCupom}
                avisoEmissao={avisoEmissao ?? avisoRegistroPendente}
                onConcluir={onConcluir}
            />
        )
    }

    // No passo da nota, sem Dialog: nada fica montado por baixo, e Dialog aberto entra com fade (.ai/knowledge/tela-cheia-em-dialog-pisca-a-tela-de-tras.md).
    if (etapa == 'emitindo') {
        return (
            <Stack sx={{ p: 4, gap: 4, width: '100%', flex: '1 0 auto', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                {imprimeResumo ? (
                    <Box>
                        <Typography variant="h3" fontWeight={800} sx={{ lineHeight: 1.15 }}>
                            Pagamento aprovado
                        </Typography>

                        <Typography variant="h6" color="text.secondary" sx={{ mt: 1.5 }}>
                            Imprimindo o resumo da sua conta.
                        </Typography>
                    </Box>
                ) : (
                    <BlocoNotaFiscalTotem
                        status={statusNota}
                        contagem={{ restante, total: SEGUNDOS_ESPERA_NFCE }}
                        impressao={impressaoCupom}
                        comSenha={false}
                    />
                )}
            </Stack>
        )
    }

    return (
        <PosPagamentoLayout valorFormatado={valorFormatado} embutido>
            <AvisoInatividade
                ativo
                msInatividade={INATIVIDADE_PERGUNTA_MS}
                aoExpirar={seguirSemResposta}
                mensagem={(segundos) => (
                    imprimirNotaSempre ? (
                        <>
                            Sem resposta, a nota fiscal sai sem CPF em <strong>{segundos}s</strong>.
                        </>
                    ) : (
                        <>
                            Sem resposta, a nota fiscal sai {cpfConfirmado ? '' : 'sem CPF e '}sem cupom impresso em{' '}
                            <strong>{segundos}s</strong>.
                        </>
                    )
                )}
                acaoSecundaria={{ rotulo: 'Concluir agora', aoClicar: seguirSemResposta }}
            />

            {etapa === 'cpf-pergunta' && (
                <CpfPergunta
                    onInformar={() => setEtapa('cpf-input')}
                    onPular={() => seguirDoCpf('')}
                />
            )}

            {etapa === 'cpf-input' && (
                <CpfInput
                    onConfirmar={seguirDoCpf}
                    onVoltar={() => setEtapa('cpf-pergunta')}
                />
            )}

            {etapa === 'impressao-pergunta' && (
                <ImpressaoPergunta
                    onImprimir={() => void emitir(true, cpf)}
                    onPular={() => void emitir(false, cpf)}
                />
            )}
        </PosPagamentoLayout>
    )
}
